<template>
  <div v-if="form">
    <!-- Media -->
    <b-media class="mb-2">
      <template #aside>
        <b-avatar
          ref="previewEl"
          :src="form.logoImgURL"
          :text="avatarText(form.campaignName)"
          variant="light-primary"
          size="100px"
          rounded
        />
      </template>
      <h4 class="mb-1">
        {{ form.campaignName }}
      </h4>
      <div class="d-flex flex-wrap">
        <b-button
          variant="primary"
          @click="imageUpload = !imageUpload"
        >
          <span class="d-none d-sm-inline">更換</span>
          <feather-icon
            icon="EditIcon"
            class="d-inline d-sm-none"
          />
        </b-button>
        <b-button
          v-if="form.logoImgURL != ''"
          variant="outline-secondary"
          class="ml-1"
          @click="removeLogoImg"
        >
          <span class="d-none d-sm-inline">移除</span>
          <feather-icon
            icon="TrashIcon"
            class="d-inline d-sm-none"
          />
        </b-button>
        <my-upload
          v-model="imageUpload"
          lang-type="zh-tw"
          field="img"
          img-format="png"
          @crop-success="cropSuccess"
        />
      </div>
    </b-media>

    <b-form>
      <b-row>
        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="活動名稱"
            label-for="campaign-name"
          >
            <b-form-input
              id="campaignName"
              v-model="form.campaignName"
              required
              @change="checkDiff"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="聯絡電話"
            label-for="contact-number"
          >
            <b-form-input
              id="contactNumber"
              v-model="form.contactNumber"
              required
              @change="checkDiff"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="4"
        >
          <b-form-group
            label="簡址"
            label-for="slug"
          >
            <b-form-input
              id="slug"
              v-model="form.slug"
              :disabled="true"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-form>

    <!-- Action Buttons -->
    <b-button
      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
      variant="primary"
      class="mb-1 mb-sm-0 mr-0 mr-sm-1"
      :block="$store.getters['app/currentBreakPoint'] === 'xs'"
      :disabled="btnDisable"
      @click="saveChanges"
    >
      <div v-if="submit">
        <b-spinner small />
        <span class="sr-only">更新中...</span>
      </div>
      <div v-else-if="!success">
        儲存變更
      </div>
      <div v-else-if="success">
        已儲存
      </div>
    </b-button>
    <b-button
      v-if="btnReset"
      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
      variant="outline-secondary"
      :block="$store.getters['app/currentBreakPoint'] === 'xs'"
      @click="setDefault"
    >
      取消
    </b-button>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  BButton, BMedia, BAvatar, BRow, BCol, BFormGroup, BFormInput, BForm, BSpinner,
} from 'bootstrap-vue'
import store from '@/store'
import { avatarText } from '@core/utils/filter'
import myUpload from 'vue-image-crop-upload/upload-2.vue'
import Ripple from 'vue-ripple-directive'

export default {
  components: {
    BButton,
    BSpinner,
    BMedia,
    BAvatar,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    myUpload,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      statusOptions: ['delete', 'suspanded'],
      avatarText,
      form: null,
      imageUpload: false,
      needUpload: false,
      btnDisable: true,
      success: false,
      update: false,
      submit: false,
    }
  },
  computed: {
    ...mapGetters(['user', 'campaign', 'userCampaigns']),
  },
  mounted() {
    this.setDefault()
  },
  methods: {
    setDefault() {
      this.form = {
        campaignName: this.campaign.campaignName,
        contactNumber: this.campaign.contactNumber,
        slug: this.campaign.slug,
        logoImgURL: this.campaign.logoImgURL,
      }
      this.needUpload = false
      this.btnDisable = true
      this.btnReset = false
      this.success = false
      this.update = false
      this.submit = false
    },
    cropSuccess(imgDataUrl) {
      this.success = false
      this.form.logoImgURL = imgDataUrl
      this.needUpload = true
      this.btnDisable = false
      this.btnReset = true
    },
    removeLogoImg() {
      this.success = false
      this.form.logoImgURL = ''
      this.needUpload = true
      this.btnDisable = false
      this.btnReset = true
    },
    checkDiff() {
      this.success = false

      if ((this.form.contactNumber !== this.campaign.contactNumber) || (this.form.campaignName !== this.campaign.campaignName)) {
        if (!this.form.contactNumber === '' || !this.form.campaignName === '') {
          this.btnDisable = true
          this.update = false
        } else {
          this.btnDisable = false
          this.update = true
        }
        this.btnReset = true
      } else {
        this.btnDisable = true
        this.update = false
        this.btnReset = false
      }
    },
    saveChanges() {
      this.submit = true
      this.btnDisable = true

      if (this.needUpload) {
        store.dispatch('uploadCampaignImage', {
          type: 'data_url',
          data: this.form.logoImgURL,
          uid: this.campaign.cuid,
          cid: this.campaign.cid,
          name: this.campaign.cid,
          colName: 'logoImgURL',
        }).then(() => {
          if (!this.update) {
            this.success = true
            this.submit = false
          }
        }).catch(() => {
          if (!this.update) {
            this.success = false
            this.submit = false
          }
        })
      }

      const updateData = {}
      if (this.form.contactNumber !== this.campaign.contactNumber) {
        updateData.contactNumber = this.form.contactNumber
      }

      if (this.form.campaignName !== this.campaign.campaignName) {
        updateData.campaignName = this.form.campaignName
      }

      if (this.update) {
        this.update = false
        store.dispatch('updateCampaign', {
          data: updateData,
          campaign: this.campaign,
          userCampaigns: this.userCampaigns,
          uid: this.user.uid,
        }).then(() => {
          this.success = true
          this.submit = false
        }).catch(() => {
          this.submit = false
        })
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
